"use client";

import * as React from "react";
import { Check, ChevronsUpDown, Eye, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../../components/ui/hover-card";

// Import the Risk type and risksService
import type { Risk as ProjectRisk } from '@/types/risks';
import { getAllRisks } from '@/services/risksService';
import { useTenancy } from '@/contexts/TenancyContext';
import { routePathByName } from "@/app/routes";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightFromSquare } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";

// Local type for the combobox
interface Risk extends Pick<ProjectRisk, 'id' | 'description' | 'category' | 'riskRating' | 'response' | 'nextReviewDate'> {
  name: string;
  originalRisk: ProjectRisk;
}

// Props for the RisksCombobox component
interface RisksComboboxProps {
  selected: string[];
  setSelected: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  onRiskSelect?: (riskId: string) => void;
  onRiskDeselect?: (riskId: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  risks?: Risk[]; // Optionally pass risks directly
  fetchRisks?: () => Promise<Risk[]>; // Or provide a function to fetch them
}

const getBadgeVariant = (level: string) => {
  switch (level.toLowerCase()) {
    case 'low':
      return 'low';
    case 'medium':
      return 'medium';
    case 'high':
      return 'high';
    case 'extreme':
      return 'extreme';
    default:
      return 'default';
  }
};


// Create a context to provide risks data
interface RisksContextType {
  risks: Risk[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<Risk[]>;
  categoryFilters: string[];
  searchRisks: (query: string, categoryFilter?: string) => Promise<void>;
}

const RisksContext = React.createContext<RisksContextType>({
  risks: [],
  loading: false,
  error: null,
  refresh: async () => [],
  categoryFilters: [],
  searchRisks: async () => {},
});

// Provider component to fetch and manage risks data
export const RisksProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [risks, setRisks] = React.useState<Risk[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);
  const [categoryFilters, setCategoryFilters] = React.useState<string[]>([]);
  const [initialized, setInitialized] = React.useState(false);
  
  // Enhanced console logging with styles
  const log = (message: string, data?: any, type: 'log' | 'warn' | 'error' | 'debug' = 'log') => {
    const styles = [
      'background: #1a365d',
      'color: white',
      'padding: 2px 8px',
      'border-radius: 4px',
      'font-weight: bold',
      'font-family: monospace',
      'font-size: 12px'
    ].join(';');
    
    const timestamp = new Date().toISOString().substring(11, 23);
    const logMessage = `%c[${timestamp}] RisksProvider: ${message}`;
    
    if (type === 'error') {
      console.error(logMessage, styles, data || '');
    } else if (type === 'warn') {
      console.warn(logMessage, styles, data || '');
    } else if (type === 'debug') {
      console.debug(logMessage, styles, data || '');
    } else {
      console.log(logMessage, styles, data || '');
    }
  };

  const { activeTenancy } = useTenancy();
  
  // Fetch risks from the service
  const fetchRisks = React.useCallback(async () => {
    if (!activeTenancy?.id) {
      log('No active tenancy ID, skipping risk fetch');
      return [];
    }

    try {
      setLoading(true);
      log('Fetching risks...', { companyID: activeTenancy.id });
      
      // Fetch risks from the service
      const fetchedRisks = await getAllRisks(activeTenancy.id);

      log('Fetched risks', { fetchedRisks });
      
      // Transform the risks to match our expected format
      const transformedRisks = fetchedRisks.map(risk => ({
        id: risk.id,
        name: risk.risk || 'Unnamed Risk',
        description: risk.risk,
        category: risk.category || 'Uncategorized',
        riskRating: risk.riskRating || 'Medium',
        originalRisk: risk // Keep the original risk data
      }));
      
      // Extract unique categories for filters
      const categories = Array.from(
        new Set(transformedRisks.map(risk => risk.category).filter(Boolean))
      ) as string[];
      
      setRisks(transformedRisks);
      setCategoryFilters(categories);
      setError(null);
      setInitialized(true);
      
      log('Successfully fetched risks', { 
        riskCount: transformedRisks.length,
        categoryCount: categories.length 
      });
      
      return transformedRisks;
    } catch (err) {
      const error = err as Error;
      log('Error fetching risks', {
        error: error.message,
        stack: error.stack,
        name: error.name
      }, 'error');
      
      setError(error);
      setRisks([]);
      throw error;
    } finally {
      setLoading(false);
      log('Fetch risks completed', { loading: false });
    }
  }, []);
  
  // Search risks with optional filtering
  const searchRisks = React.useCallback(async (query: string, categoryFilter?: string) => {
    try {
      setLoading(true);
      
      // Get fresh risks from the fetchRisks function
      const allRisks = await fetchRisks();
      
      // Filter risks based on search query and category
      const filtered = allRisks.filter(risk => {
        const matchesSearch = !query || 
          risk.name.toLowerCase().includes(query.toLowerCase()) ||
          (risk.description && risk.description.toLowerCase().includes(query.toLowerCase()));
          
        const matchesCategory = !categoryFilter || risk.category === categoryFilter;
        
        return matchesSearch && matchesCategory;
      });
      
      setRisks(filtered);
    } catch (err) {
      const error = err as Error;
      log('Error searching risks', error, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchRisks]);
  
  // Initial load and reload when tenancy changes
  React.useEffect(() => {
    if (activeTenancy?.id && !initialized) {
      log('Active tenancy changed, fetching risks...', { companyID: activeTenancy.id });
      fetchRisks().catch(error => {
        log('Error fetching risks:', error, 'error');
        setError(error instanceof Error ? error : new Error('Failed to fetch risks'));
      });
    }
  }, [fetchRisks, initialized, activeTenancy?.id]);
  
  // Refresh function to be exposed via context
  const refresh = React.useCallback(async () => {
    log('Refreshing risks...');
    return fetchRisks();
  }, [fetchRisks]);
  
  return (
    <RisksContext.Provider value={{
      risks,
      loading,
      error,
      refresh,
      categoryFilters,
      searchRisks,
    }}>
      {children}
    </RisksContext.Provider>
  );
};

// Hook to use the risks context
export const useRisks = (): RisksContextType => {
  const context = React.useContext(RisksContext);
  if (!context) {
    throw new Error('useRisks must be used within a RisksProvider');
  }
  return context;
};

// Main RisksCombobox component
export function RisksCombobox({
  selected,
  setSelected,
  placeholder = "Select risks...",
  className,
  fullWidth = false,
  onRiskSelect,
  onRiskDeselect,
  onAllSelect,
  onAllDeselect,
  risks: propRisks,
  fetchRisks: propFetchRisks,
}: RisksComboboxProps) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [activeCategory, setActiveCategory] = React.useState<string | null>(null);
  const [isTyping, setIsTyping] = React.useState(false);
  
  // Use context if props not provided
  const context = React.useContext(RisksContext);
  const { 
    risks: contextRisks = [], 
    loading: contextLoading, 
    error: contextError,
    categoryFilters,
    searchRisks: contextSearchRisks 
  } = context || {};
  
  // Use provided risks/fetch function or fall back to context
  const risks = propRisks || contextRisks;
  const loading = propRisks ? false : contextLoading;
  const error = propRisks ? null : contextError;
  
  // Debounce search
  React.useEffect(() => {
    if (!propRisks) { // Only search if using context
      const timer = setTimeout(() => {
        if (isTyping) {
          contextSearchRisks(searchQuery, activeCategory || undefined).catch(console.error);
          setIsTyping(false);
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [searchQuery, activeCategory, isTyping, propRisks, contextSearchRisks]);
  
  // Toggle risk selection
  const toggleRisk = (riskId: string) => {
    const newSelected = selected.includes(riskId)
      ? selected.filter(id => id !== riskId)
      : [...selected, riskId];
      
    setSelected(newSelected);
    
    // Call appropriate callback
    if (selected.includes(riskId)) {
      onRiskDeselect?.(riskId);
    } else {
      onRiskSelect?.(riskId);
    }
  };
  
  // Select/deselect all
  const handleSelectAll = () => {
    if (selected.length === risks.length) {
      setSelected([]);
      onAllDeselect?.();
    } else {
      const allIds = risks.map(risk => risk.id);
      setSelected(allIds);
      onAllSelect?.();
    }
  };
  
  // Filter risks based on search query and active category
  const filteredRisks = React.useMemo(() => {
    if (propRisks) {
      return propRisks.filter(risk => {
        const matchesSearch = !searchQuery || 
          risk.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (risk.description && risk.description.toLowerCase().includes(searchQuery.toLowerCase()));
          
        const matchesCategory = !activeCategory || risk.category === activeCategory;
        
        return matchesSearch && matchesCategory;
      });
    }
    return risks;
  }, [propRisks, risks, searchQuery, activeCategory]);
  
  // Group risks by category for better organization
  const risksByCategory = React.useMemo(() => {
    const grouped: Record<string, Risk[]> = {};
    
    filteredRisks.forEach(risk => {
      const category = risk.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(risk);
    });
    
    return Object.entries(grouped).sort(([catA], [catB]) => catA.localeCompare(catB));
  }, [filteredRisks]);
  
  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setIsTyping(true);
  };
  
  // Toggle category filter
  const toggleCategory = (category: string) => {
    setActiveCategory(activeCategory === category ? null : category);
  };
  
  // Remove a selected risk
  const removeSelected = (riskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = selected.filter(id => id !== riskId);
    setSelected(newSelected);
    onRiskDeselect?.(riskId);
  };
  
  // Clear all selected risks
  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected([]);
    onAllDeselect?.();
  };
  
  // Get risk by ID
  const getRiskById = (id: string) => risks.find(risk => risk.id === id);
  
  return (
    <div className={cn("relative", fullWidth ? 'w-full' : 'w-[400px]', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between hover:bg-primary/2",
              selected.length > 0 ? "h-auto py-1" : "h-10"
            )}
          >
            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
              {selected.length > 0 ? (
                selected.map((riskId) => {
                  const risk = risks.find((r) => r.id === riskId);
                  if (!risk) return null;
                  
                  return (
                    <HoverCard key={riskId}>
                      <HoverCardTrigger asChild>
                        <div className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm cursor-help">
                          <div className="flex items-center gap-2">
                            {/* Open Icon to open in risk detail */}
                            <button onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              window.open(routePathByName('risk.detail', { id: riskId }), '_blank');
                            }} className="opacity-50 hover:opacity-100 flex items-center justify-center h-4 w-4">
                              <FontAwesomeIcon icon={faUpRightFromSquare} className="h-3 w-3" />
                            </button>
                            <span className="max-w-[200px] truncate text-sm">{risk.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSelected(riskId, e);
                            }}
                            className="opacity-50 hover:opacity-100 flex items-center justify-center h-4 w-4"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80 p-4 space-y-2" align="start" side="top">
                        <div className="space-y-1">
                          <h4 className="text-xs font-semibold">{risk.name}</h4>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="procedure">ID: {risk.id}</Badge>
                            {risk.riskRating && (
                              <>
                                <Badge variant={getBadgeVariant(risk.riskRating)}>Risk Rating: {risk.riskRating}</Badge>
                              </>
                            )}
                          </div>
                          {risk.category && (
                            <div className="text-xxs">
                              <Badge variant="policy">Category: {risk.category}</Badge>
                            </div>
                          )}
                          {/* {risk.description && (
                            <div className="text-sm mt-2">
                              <p className="line-clamp-3">{risk.description}</p>
                            </div>
                          )} */}
                          {risk.response && (
                            <div className="text-xxs mt-2">
                              <Badge variant="presentation">Response: {risk.response}</Badge>
                            </div>
                          )}
                          {risk.nextReviewDate && (
                            <div className="text-xxs mt-2">
                              <Badge variant="plan">Next Review: {new Date(risk.nextReviewDate).toLocaleDateString()}</Badge>
                            </div>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  );
                })
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search risks..."
              value={searchQuery}
              onValueChange={handleSearchChange}
              className="border-0 focus:ring-0"
            />
            <CommandList 
              className="max-h-[400px] overflow-auto"
              onWheel={(e) => {
                // Prevent wheel events from propagating to parent elements
                e.stopPropagation();
              }}
            >
              {/* Category Filter Buttons */}
              {categoryFilters.length > 0 && (
                <div className="flex flex-wrap gap-1 p-2 border-b overflow-auto max-h-[120px]">
                  <button
                    type="button"
                    onClick={() => setActiveCategory(null)}
                    className={cn(
                      "text-xs px-2 py-1 rounded-md whitespace-nowrap",
                      !activeCategory 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted hover:bg-muted/80"
                    )}
                  >
                    All
                  </button>
                  {categoryFilters.map(category => (
                    <button
                      key={category}
                      type="button"
                      onClick={() => toggleCategory(category)}
                      className={cn(
                        "text-xs px-2 py-1 rounded-md whitespace-nowrap",
                        activeCategory === category
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-muted/80"
                      )}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
              
              {loading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Loading risks...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-destructive">
                  Error loading risks. Please try again.
                </div>
              ) : filteredRisks.length === 0 ? (
                <CommandEmpty>No risks found.</CommandEmpty>
              ) : (
                <CommandGroup className="max-h-[300px] overflow-y-auto">
                  {risksByCategory.map(([category, categoryRisks]) => (
                    <div key={category} className="border-b last:border-b-0">
                      <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground bg-muted/50">
                        {category}
                      </div>
                      {categoryRisks.map((risk) => {
                        const isSelected = selected.includes(risk.id);
                        return (
                          <CommandItem
                            key={risk.id}
                            value={risk.id}
                            onSelect={() => toggleRisk(risk.id)}
                            className="cursor-pointer"
                          >
                            <div className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                              isSelected
                                ? "bg-primary text-primary-foreground"
                                : "opacity-50 [&_svg]:invisible"
                            )}>
                              <Check className={"h-4 w-4"} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{risk.name}</span>
                                {risk.riskRating && (
                                  <Badge variant="outline" className="text-xs">
                                    {risk.riskRating}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </div>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
